import type { NDateLocale } from 'naive-ui'

import type { LangEnum } from '@/constants/locales'
import { dateFnsLanguageMap, DEFAULT_LANG, supportedLangsByCode, supportedLangsByName } from '@/constants/locales'
import { GtmEvent } from '@/types/gtm'
import { checkLangSupported } from '@/utils/intl'

import { useRegionStore } from '@/store/region'

export function useRegionNames() {
  const { locale } = useI18n()
  const regionNames = computed(() => new Intl.DisplayNames([locale.value], { type: 'region' }))

  function getRegionName(countryCode = 'TW') {
    try {
      return regionNames.value.of(countryCode) ?? ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  return {
    getRegionName,
  }
}

async function getNUIDateLocale(newLocale: LangEnum): Promise<NDateLocale> {
  const dateFnsModule = await dateFnsLanguageMap[newLocale]()

  return {
    name: supportedLangsByCode[newLocale]?.name ?? DEFAULT_LANG,
    locale: dateFnsModule.default,
  }
}

export function useNUILocale() {
  const { locale } = useI18n()
  const nUIDateLocale = ref<Nullable<NDateLocale>>(null)

  watch(locale, updateNUILocale, { immediate: true })

  async function updateNUILocale(newLocale: string) {
    try {
      const dateModule = await getNUIDateLocale(newLocale as LangEnum)

      nUIDateLocale.value = dateModule
    } catch (error: any) {
      console.warn(`[useNUILocale] locale "${newLocale}" update failed, error: ${error.message}`)
    }
  }

  return { nUIDateLocale }
}

export function useLangSelector() {
  const nuxtApp = useNuxtApp()
  const { locale, setLocale } = useI18n()
  const route = useRoute()
  const router = useRouter()
  const { loadLocaleMessages } = useLocaleMessages()

  const regionStore = useRegionStore()

  // Use API region languages `code` (e.g. "zh-HK") to map to FE locale `name`
  const allowLangOptions = computed(() => {
    const languages = regionStore.currentRegion?.languages ?? []

    return languages.flatMap(lang => {
      if (!lang.code || !lang.name) {
        return []
      }

      // BE `code` (e.g. 'zh-HK') corresponds to FE `name`; FE `code` is the short locale id (e.g. 'zh-hk')
      const supported = supportedLangsByName[lang.code]

      return supported ? [{ label: lang.name, value: supported.code as LangEnum }] : []
    })
  })
  const selectedLangOption = computed(() => allowLangOptions.value.find(option => option.value === locale.value))

  async function changeLang(lang: LangEnum) {
    if (!lang || lang === locale.value || !checkLangSupported(lang)) {
      return
    }

    const path = replaceLangInPath(route.fullPath, lang)

    await loadLocaleMessages(lang)
    await setLocale(lang)
    router.push({ path, query: route.query, hash: route.hash })
  }

  async function handleLangLabelClick(langOption: Record<string, string>, fn = () => {}) {
    await changeLang(langOption.value as LangEnum)

    // `LANGUAGE_CHANGED` event records a real click from the user, so that's why it isn't placed in the `watch`
    nuxtApp.$trackEvent({
      event: GtmEvent.LANGUAGE_CHANGED,
      payload: {
        toLangName: supportedLangsByCode[langOption.value].name,
      },
    })

    fn()
  }

  return { locale, selectedLangOption, allowLangOptions, changeLang, handleLangLabelClick }
}

export function useUpperCase(str: ReadonlyRef<string>) {
  const { localeProperties } = useI18n()

  const localeUpperCaseString = computed(() => {
    if (!str.value) {
      return ''
    }

    try {
      const defaultLocaleIso = localeProperties.value.language
      const res = str.value.toLocaleUpperCase(defaultLocaleIso)
      return res
    } catch (error) {
      console.error(error)
      return ''
    }
  })

  return {
    localeUpperCaseString,
  }
}

export function useLowerCase(str: ReadonlyRef<string>) {
  const { localeProperties } = useI18n()

  const localeLowerCaseString = computed(() => {
    if (!str.value) {
      return ''
    }
    try {
      const defaultLocaleIso = localeProperties.value.language
      const res = str.value.toLocaleLowerCase(defaultLocaleIso)
      return res
    } catch (error) {
      console.error(error)
      return ''
    }
  })

  return {
    localeLowerCaseString,
  }
}
